/**
 * apiError — normalize FastAPI / Pydantic error payloads into a plain, readable
 * string that is always safe to render as a React child or feed to toast().
 *
 * WHY THIS EXISTS (URGENT-prod-campaign-create-react31-sep10):
 * FastAPI validation failures (HTTP 422) return `detail` as an ARRAY of Pydantic
 * v2 error objects, each shaped like:
 *     { type, loc: [...], msg, input, ctx }
 * The wizard code historically did `err.response?.data?.detail` and then either
 * put that value into a string state var rendered as `{launchError}` or passed it
 * to `toast.error(...)`. When `detail` is an array/object (422), React tries to
 * render an object as a child → **Minified React error #31** → the inner
 * ErrorBoundary shows a message-less "Algo salió mal / Something went wrong", so a
 * recoverable, explainable validation rejection becomes an undiagnosable crash.
 *
 * `detailToMessage(detail)` converts ANY of these shapes into readable field text:
 *   - string                                   → returned as-is
 *   - Pydantic array [{loc, msg}, ...]          → "field: msg; field2: msg2"
 *   - single Pydantic object { loc, msg }       → "field: msg"
 *   - object with a `.detail`/`.message`/`.msg` → recurse / use that
 *   - anything else                             → a bounded JSON string (last resort)
 *
 * It NEVER returns an object, so callers can safely render the result. It never
 * throws. It does NOT stringify a full customer payload — for Pydantic errors it
 * uses only `loc` (the field path) and `msg` (the human message), which never
 * contain the submitted `input` value.
 *
 * `extractApiError(err, fallback)` is the axios-aware wrapper: it reads
 * `err.response.data.detail` and normalizes it, falling back to a provided
 * translated string when there is no usable server detail.
 */

/**
 * Turn one Pydantic v2 error object into "field.path: message".
 * `loc` is like ["body", "retry_delay_minutes"]; we drop the leading
 * "body"/"query"/"path" scope token so the user sees the field name they know.
 */
function pydanticItemToText(item) {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  const msg =
    typeof item.msg === 'string'
      ? item.msg
      : typeof item.message === 'string'
      ? item.message
      : '';
  let field = '';
  if (Array.isArray(item.loc) && item.loc.length > 0) {
    const parts = item.loc.filter(
      (p) => p !== 'body' && p !== 'query' && p !== 'path'
    );
    field = parts.join('.');
  }
  if (field && msg) return `${field}: ${msg}`;
  if (msg) return msg;
  if (field) return field;
  // Unknown object shape — bounded, safe stringification (never render raw object).
  try {
    return JSON.stringify(item);
  } catch (e) {
    return String(item);
  }
}

/**
 * Normalize any FastAPI `detail` (or arbitrary error body) into a safe string.
 * Always returns a string; never returns/throws an object.
 */
export function detailToMessage(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'number' || typeof detail === 'boolean') {
    return String(detail);
  }

  // FastAPI 422: array of Pydantic error objects.
  if (Array.isArray(detail)) {
    const parts = detail.map(pydanticItemToText).filter(Boolean);
    if (parts.length > 0) return parts.join('; ');
    try {
      return JSON.stringify(detail);
    } catch (e) {
      return String(detail);
    }
  }

  // Object: could be a single Pydantic item, or a wrapper like {detail}/{message}.
  if (typeof detail === 'object') {
    if ('detail' in detail && detail.detail !== detail) {
      return detailToMessage(detail.detail);
    }
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.msg === 'string' || Array.isArray(detail.loc)) {
      return pydanticItemToText(detail);
    }
    try {
      return JSON.stringify(detail);
    } catch (e) {
      return String(detail);
    }
  }

  return String(detail);
}

/**
 * axios-aware: pull a safe, readable message out of an error, or fall back.
 * Use everywhere the old code did `err.response?.data?.detail || t('...')`.
 */
export function extractApiError(err, fallback = '') {
  const detail = err?.response?.data?.detail;
  const msg = detailToMessage(detail);
  if (msg) return msg;
  // Some endpoints put the message directly on data (no `detail` wrapper).
  const dataMsg = detailToMessage(err?.response?.data);
  if (dataMsg) return dataMsg;
  return fallback;
}

export default detailToMessage;
